import React, { useRef } from 'react';
import { X, Download, Phone, Wifi, Radio, Tablet as TabletIcon, Wrench, type LucideIcon } from 'lucide-react';
import { QuoteState, SolutionType, deviceMrcCalc, deviceOneTimeCalc, promoMonthlyCalc, promoOnetimeCalc } from '../types';

interface Props {
  quote:   QuoteState;
  onClose: () => void;
}

const TYPE_ICONS: Record<string, LucideIcon> = {
  [SolutionType.VOICE]:       Phone,
  [SolutionType.INTERNET]:    Wifi,
  [SolutionType.IOT]:         Radio,
  [SolutionType.TABLET]:      TabletIcon,
  [SolutionType.PRO_INSTALL]: Wrench,
};

export const QuoteOutput: React.FC<Props> = ({ quote, onClose }) => {
  const printRef = useRef<HTMLDivElement>(null);

  // Use the same calculation helpers as SummaryPanel for consistency
  const baseMrc     = quote.items.reduce((s, i) => s + i.mrc * i.lines, 0);
  const deviceMrc   = quote.items.reduce((s, i) => s + i.devices.reduce((acc, d) => acc + deviceMrcCalc(d), 0), 0);
  const monthlyDisc = quote.promos.reduce((s, p) => s + promoMonthlyCalc(p), 0);
  const onetimeDisc = quote.promos.reduce((s, p) => s + promoOnetimeCalc(p), 0);
  const netMrc      = Math.max(0, baseMrc + deviceMrc - monthlyDisc);
  const deviceOneTime = quote.items.reduce((s, i) => s + i.devices.reduce((acc, d) => acc + deviceOneTimeCalc(d), 0), 0);
  const termTotal   = (netMrc * quote.term) + deviceOneTime - onetimeDisc;

  const today       = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const orgName     = quote.sellerInfo.orgName?.trim() || '';
  const pdfName     = `Business_Proposal_${quote.customerName || 'Draft'}_${new Date().toISOString().split('T')[0]}.pdf`;


  const handleDownload = async () => {
    const html2pdf = (await import('html2pdf.js')).default;
    const el = printRef.current;
    if (!el) return;
    html2pdf().set({
      margin:      [10, 10],
      filename:    pdfName,
      image:       { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF:       { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(el).save();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 px-4">
      {/* Toolbar */}
      <div className="fixed top-4 right-4 flex items-center gap-2 z-50">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold text-sm transition-all shadow-lg"
        >
          <Download size={15} /> Download PDF
        </button>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
        >
          <X size={16} className="text-white" />
        </button>
      </div>

      {/* ── Printable document ─────────────────────────────────────────── */}
      <div
        ref={printRef}
        className="w-full max-w-3xl bg-white text-gray-900 rounded-2xl overflow-hidden shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {/* Header — seller's org brand, no platform branding */}
        <div style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 60%, #3730a3 100%)', padding: '36px 40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Left: seller org (their brand) */}
            <div>
              {orgName ? (
                <>
                  <div style={{ fontSize: 24, fontWeight: 900, color: 'white', letterSpacing: '-0.3px' }}>
                    {orgName}
                  </div>
                  {quote.sellerInfo.name && (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                      {quote.sellerInfo.name}{quote.sellerInfo.title ? ` · ${quote.sellerInfo.title}` : ''}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 20, fontWeight: 800, color: 'white' }}>Business Solutions</div>
              )}
              {quote.sellerInfo.email && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
                  {quote.sellerInfo.email}
                  {quote.sellerInfo.phone ? `  ·  ${quote.sellerInfo.phone}` : ''}
                </div>
              )}
            </div>
            {/* Right: document type + date */}
            <div style={{ textAlign: 'right', color: 'rgba(255,255,255,0.85)' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'white' }}>Business Proposal</div>
              <div style={{ fontSize: 11, marginTop: 6 }}>Date: {today}</div>
              <div style={{ fontSize: 11, marginTop: 2 }}>Valid for 30 days</div>
            </div>
          </div>
        </div>

        {/* Prepared For / By */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ padding: '24px 32px', borderRight: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
              Prepared For
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{quote.customerName || '—'}</div>
            {quote.companyName && (
              <div style={{ fontSize: 14, color: '#6B7280', marginTop: 2 }}>{quote.companyName}</div>
            )}
          </div>
          <div style={{ padding: '24px 32px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
              Your Representative
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{quote.sellerInfo.name || '—'}</div>
            {quote.sellerInfo.title && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>{quote.sellerInfo.title}</div>
            )}
            {quote.sellerInfo.email && (
              <div style={{ fontSize: 12, color: '#4338CA', marginTop: 2 }}>{quote.sellerInfo.email}</div>
            )}
            {quote.sellerInfo.phone && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>{quote.sellerInfo.phone}</div>
            )}
          </div>
        </div>

        {/* Agreement term banner */}
        <div style={{ background: '#F9FAFB', padding: '12px 32px', borderBottom: '1px solid #E5E7EB', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24 }}>
          <div>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>Agreement Term: </span>
            <span style={{ fontWeight: 700, color: '#111827' }}>{quote.term} Month{quote.term !== 1 ? 's' : ''}</span>
          </div>
          <div>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>Monthly Total: </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#4338CA' }}>${netMrc.toFixed(2)}</span>
          </div>
          {monthlyDisc > 0 && (
            <div>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>Promo Savings: </span>
              <span style={{ fontWeight: 700, color: '#059669' }}>-${monthlyDisc.toFixed(2)}/mo</span>
            </div>
          )}
        </div>

        {/* Line items table */}
        <div style={{ padding: '24px 32px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Solution Details
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F3F4F6' }}>
                {['Solution', 'Plan', 'Lines / Units', 'MRC / Unit', 'Monthly Subtotal'].map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                      textAlign: h === 'Monthly Subtotal' ? 'right' : 'left',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quote.items.map((item, i) => (
                <React.Fragment key={item.id}>
                  <tr style={{ borderBottom: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={{ padding: '12px', fontSize: 12, fontWeight: 700, color: '#111827' }}>{item.type}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#6B7280' }}>{item.planName}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#374151', fontWeight: 600 }}>{item.lines}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#374151' }}>${item.mrc.toFixed(2)}</td>
                    <td style={{ padding: '12px', fontSize: 13, color: '#4338CA', fontWeight: 700, textAlign: 'right' }}>
                      ${(item.mrc * item.lines).toFixed(2)}/mo
                    </td>
                  </tr>
                  {item.devices.map(d => {
                    const mrc    = deviceMrcCalc(d);
                    const termLbl = d.financingTerm === 'FRP' ? 'FRP' : `${d.financingTerm}-mo`;
                    return (
                      <tr key={d.id} style={{ background: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '6px 12px 6px 28px', fontSize: 11, color: '#9CA3AF' }} colSpan={2}>
                          ↳ {d.name}
                          <span style={{ marginLeft: 6, fontSize: 10, color: '#C4B5FD', background: '#EDE9FE', padding: '1px 5px', borderRadius: 4 }}>
                            {termLbl}
                          </span>
                        </td>
                        <td style={{ padding: '6px 12px', fontSize: 11, color: '#6B7280' }}>{d.count}</td>
                        <td style={{ padding: '6px 12px', fontSize: 11, color: '#6B7280' }}>
                          ${d.cost.toFixed(2)}{d.financingTerm !== 'FRP' ? ' total' : ''}
                        </td>
                        <td style={{ padding: '6px 12px', fontSize: 11, color: '#6B7280', textAlign: 'right' }}>
                          ${mrc.toFixed(2)}/mo
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Promotions */}
        {quote.promos.length > 0 && (
          <div style={{ padding: '0 32px 20px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Promotions Applied</div>
            {quote.promos.map(p => {
              const monthly  = promoMonthlyCalc(p);
              const onetime  = promoOnetimeCalc(p);
              const totalVal = p.value * (p.lines ?? 1);
              return (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #E5E7EB', fontSize: 12 }}>
                  <span style={{ color: '#059669' }}>
                    {p.name}
                    {p.type === 'monthly' && p.duration
                      ? ` ($${p.value.toFixed(2)} × ${p.lines ?? 1} lines ÷ ${p.duration} mo)`
                      : ` ($${p.value.toFixed(2)} × ${p.lines ?? 1} lines)`}
                  </span>
                  <span style={{ fontWeight: 700, color: '#059669' }}>
                    {p.type === 'monthly'
                      ? `-$${monthly.toFixed(2)}/mo`
                      : `-$${onetime.toFixed(2)} one-time`}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals */}
        <div style={{ background: '#F9FAFB', borderTop: '2px solid #E5E7EB', padding: '20px 32px' }}>
          <div style={{ maxWidth: 300, marginLeft: 'auto' }}>
            {([
              ['Service MRC',    `$${baseMrc.toFixed(2)}/mo`,     false],
              ['Device MRC',     `$${deviceMrc.toFixed(2)}/mo`,   false],
              ...(deviceOneTime > 0 ? [['Upfront Device Fees', `$${deviceOneTime.toFixed(2)}`, false]] : []),
              ...(monthlyDisc > 0 ? [['Monthly Promotions', `-$${monthlyDisc.toFixed(2)}/mo`, true]] : []),
              ...(onetimeDisc > 0 ? [['One-Time Discount',  `-$${onetimeDisc.toFixed(2)}`,   true]] : []),
            ] as [string, string, boolean][]).map(([l, v, g]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                <span style={{ color: '#6B7280' }}>{l}</span>
                <span style={{ color: g ? '#059669' : '#374151', fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '2px solid #E5E7EB', fontSize: 16, fontWeight: 900 }}>
              <span style={{ color: '#111827' }}>Net Monthly Total</span>
              <span style={{ color: '#4338CA' }}>${netMrc.toFixed(2)}/mo</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12 }}>
              <span style={{ color: '#9CA3AF' }}>{quote.term}-Month Agreement Total</span>
              <span style={{ fontWeight: 700, color: '#374151' }}>${termTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Solution narrative (if AI generated) */}
        {quote.insights?.narrative && (
          <div style={{ padding: '20px 32px', borderTop: '1px solid #E5E7EB', background: '#FAFAFA' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
              Solution Overview
            </div>
            <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.7, margin: 0 }}>{quote.insights.narrative}</p>
          </div>
        )}

        {/* Footer — neutral, no platform branding */}
        <div style={{ background: '#1E1B4B', padding: '14px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.6 }}>
            Confidential &nbsp;·&nbsp; This proposal is valid for 30 days from the date of issue.
          </div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {today}
          </div>
        </div>
      </div>


    </div>
  );
};
